import React from "react";
import { motion } from "framer-motion";
import { Reveal } from "../Reveal";

export const About = ({ data }) => {
  const about = data?.about || {};
  const chapters = about.chapters || [];
  const profile = about.profile || {};
  const photos = profile.photos || [];

  return (
    <section id="about" className="relative bg-void py-16 md:py-24" data-testid="about-section">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        {/* Section label */}
        <Reveal className="mb-16 flex items-center gap-4">
          <span className="h-px w-12 bg-coral" />
          <span className="text-coral text-xs uppercase tracking-[0.3em]">About Us</span>
        </Reveal>

        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Left: heading + founder photos + bio */}
          <div className="lg:col-span-5">
            <Reveal>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
                {about.heading}
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-6 text-smoke text-base md:text-lg leading-relaxed">
                {about.intro}
              </p>
            </Reveal>

            {/* Founder photos — collage */}
            <Reveal delay={0.2} className="mt-6">
              <div className="grid grid-cols-2 grid-rows-2 gap-3 h-[360px]" data-testid="founder-photos">
                {photos.slice(0, 3).map((src, i) => (
                  <div
                    key={i}
                    className={`group relative overflow-hidden rounded-lg ${
                      i === 0 ? "row-span-2" : ""
                    }`}
                  >
                    <img
                      src={src}
                      alt={`${profile.name || "Founder"} ${i + 1}`}
                      className="h-full w-full object-cover brightness-75 transition-all duration-700 group-hover:brightness-110 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 ring-1 ring-inset ring-gold/20 rounded-lg" />
                  </div>
                ))}
              </div>
            </Reveal>

            {/* Founder bio */}
            <Reveal delay={0.3} className="mt-6">
              <div className="border-l-2 border-gold pl-4">
                <h3 className="font-display text-xl text-ink">{profile.name}</h3>
                <p className="text-coral text-[10px] uppercase tracking-[0.25em] mt-1">{profile.role}</p>
                <p className="text-smoke text-sm leading-relaxed mt-3">{profile.bio}</p>
              </div>
            </Reveal>
          </div>

          {/* Right: numbered manifesto chapters */}
          <div className="lg:col-span-6 lg:col-start-7 flex flex-col">
            {chapters.map((ch, i) => (
              <Reveal key={ch.number || i} delay={i * 0.1}>
                <div className="group border-t border-line py-10 transition-colors duration-500 hover:border-gold/50">
                  <div className="flex items-start gap-6 md:gap-10">
                    <span className="font-body font-bold text-5xl md:text-6xl text-outline transition-all duration-500 group-hover:text-outline-gold shrink-0">
                      {ch.number}
                    </span>
                    <div>
                      <h3 className="font-body font-semibold text-2xl md:text-3xl text-ink mb-3">
                        {ch.title}
                      </h3>
                      <p className="text-smoke leading-relaxed max-w-md">{ch.text}</p>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
